import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import { keyframes } from '@emotion/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import api from 'services/api'
import { outcomeInk, outcomeOf } from '../Moments/LedgerTable'
import { MOMENT_OUTCOMES } from '../Moments/vocabulary'
import {
  DECISIONS,
  decisionOf,
  fmtCount,
  fmtPct,
  fmtSeconds,
} from './vocabulary'

// Spans coming in: every account's routed spans, newest routed first, as the
// router left them. The server puts no account on a row — no id, no name — so
// nothing here opens anyone's audio or words; one account's own record is
// "What the router heard" below, under the playback policy.
//
// It polls on the page's cadence and slides new spans in at the top. A span
// arrives minutes after it seals, once the router has run, and its cards and
// cost fill in on later polls as transcription and selection finish.
const PAGE = 40
const POLL_INTERVAL_MS = 60_000
// A strip's full width is this much audio, so a short span reads short beside
// a long one. A longer span is squeezed into the full width.
const FULL_WIDTH_MS = 30 * 60_000
const COLUMNS = '92px 48px 56px 56px 40px 76px'

const rowKey = (row) => `${row.routed_at}|${row.start_ms}`
// `routed_at` is Python's isoformat, which drops the fraction on a whole
// second, so the strings do not sort; the instants do.
const instant = (iso) => Date.parse(iso)
const usd = (value) => `$${value.toFixed(4)}`

const arrive = keyframes`
  from { background-color: var(--arrive); }
  to { background-color: transparent; }
`

const cardsOf = (row) => {
  const selection = row.selection
  if (!selection) {
    return {
      text: '—',
      tip:
        row.transcription === 'transcribed'
          ? 'Transcribed, not selected yet'
          : `No selection yet · transcript ${row.transcription.replace(
              /_/g,
              ' '
            )}`,
    }
  }
  if (selection.terminal || selection.state === 'failed') {
    return {
      text: '!',
      tone: 'error.main',
      tip: `Selection ${selection.state}`,
    }
  }
  if (!selection.selected) {
    return {
      text: '…',
      tip: selection.state === 'running' ? 'Selecting' : 'Selection queued',
    }
  }
  const cards = selection.outcomes.cut || 0
  const found = selection.moments.length
  const tally = MOMENT_OUTCOMES.filter((o) => selection.outcomes[o.key])
    .map((o) => `${o.label} ${selection.outcomes[o.key]}`)
    .join(' · ')
  return {
    text: String(cards),
    tone: cards ? 'success.main' : undefined,
    tip: found ? `${found} found · ${tally}` : 'Nothing found',
  }
}

const costOf = (row) =>
  row.cost_usd == null
    ? { text: '—', tip: 'Nothing billed yet' }
    : {
        text: `${row.cost_floor ? '≥ ' : ''}${usd(row.cost_usd)}`,
        tip: row.cost_floor
          ? 'Selection only: the transcript doc is gone (30 days), so transcription is not counted'
          : 'Transcription plus selection',
      }

const Cell = ({ tip, tone, children }) => (
  <Tooltip title={tip || ''} placement="top" disableInteractive>
    <Box
      component="span"
      sx={{ color: tone || 'text.primary', cursor: 'default' }}
    >
      {children}
    </Box>
  </Tooltip>
)

// One cell per minute, filled in its decision's colour, and under it a lane
// where each found moment is a bar in its outcome's ink — below the cells
// rather than on them, because a cut's green would vanish on a routed minute.
const FleetStrip = ({ row }) => {
  const theme = useTheme()
  const segments = row.segments
  const total = segments.reduce((sum, s) => sum + (s.duration_ms || 60_000), 0)
  const marks = row.selection?.moments || []
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1px',
        width: `${Math.min(1, total / FULL_WIDTH_MS) * 100}%`,
        minWidth: 4,
      }}
    >
      {segments.map((segment, index) => {
        const decision = decisionOf(segment)
        const length = segment.duration_ms || 60_000
        const start = segment.start_ms
        const found =
          start == null
            ? []
            : marks.filter(
                (mark) => mark.start_ms < start + length && mark.end_ms > start
              )
        return (
          <Tooltip
            key={index}
            placement="top"
            disableInteractive
            title={
              <Box sx={{ fontSize: 12 }}>
                {start != null && <b>{dayjs(start).format('HH:mm')} · </b>}
                {decision.label}
                {found.map((mark, at) => (
                  <Box key={at}>moment · {outcomeOf(mark.outcome).label}</Box>
                ))}
              </Box>
            }
          >
            <Box
              sx={{
                flex: `${length / total} 1 0`,
                minWidth: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              <Box
                sx={{
                  height: 14,
                  borderRadius: '2px',
                  bgcolor: segment.expired
                    ? 'transparent'
                    : alpha(
                        decision.color,
                        decision === DECISIONS.skip ? 0.45 : 0.9
                      ),
                  border: segment.expired
                    ? `1px dashed ${decision.color}`
                    : 'none',
                }}
              />
              <Box sx={{ position: 'relative', height: 4 }}>
                {found.map((mark, at) => {
                  const left = Math.max(0, (mark.start_ms - start) / length)
                  const right = Math.min(1, (mark.end_ms - start) / length)
                  return (
                    <Box
                      key={at}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${left * 100}%`,
                        width: `${Math.max(0, right - left) * 100}%`,
                        borderRadius: '1px',
                        bgcolor: outcomeInk(theme, mark.outcome),
                      }}
                    />
                  )
                })}
              </Box>
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

const FleetRow = ({ row, fresh }) => {
  const theme = useTheme()
  const cards = cardsOf(row)
  const cost = costOf(row)
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 2,
        rowGap: 0.5,
        py: 0.75,
        px: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '--arrive': alpha(theme.palette.success.main, 0.22),
        animation: fresh ? `${arrive} 4s ease-out` : 'none',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: COLUMNS,
          columnGap: 1.5,
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Cell
          tip={`Recorded; routed ${dayjs(row.routed_at).format('HH:mm:ss')}`}
        >
          {dayjs(row.start_ms).format('ddd HH:mm')}
        </Cell>
        <Cell>{fmtSeconds((row.duration_ms || 0) / 1000)}</Cell>
        <Cell>{fmtPct(row.speech_fraction)}</Cell>
        <Cell tone={row.routed_fraction ? 'success.main' : undefined}>
          {fmtPct(row.routed_fraction)}
        </Cell>
        <Cell tip={cards.tip} tone={cards.tone}>
          {cards.text}
        </Cell>
        <Cell tip={cost.tip}>{cost.text}</Cell>
      </Box>
      <Box sx={{ flex: '1 1 240px', minWidth: 160 }}>
        <FleetStrip row={row} />
      </Box>
    </Box>
  )
}

const Swatch = ({ color, label, help, dashed }) => (
  <Tooltip title={help || ''} placement="top">
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        cursor: 'default',
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 10,
          borderRadius: '2px',
          bgcolor: dashed ? 'transparent' : color,
          border: dashed ? `1px dashed ${color}` : 'none',
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Tooltip>
)

const Legend = () => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 2,
        rowGap: 0.5,
        mb: 1.5,
      }}
    >
      {Object.values(DECISIONS).map(({ label, color, help }) => (
        <Swatch
          key={label}
          label={label}
          help={help}
          dashed={color === DECISIONS.expired.color}
          color={alpha(color, color === DECISIONS.skip.color ? 0.45 : 0.9)}
        />
      ))}
      <Typography variant="caption" color="text.secondary">
        · moments, under the minutes:
      </Typography>
      {MOMENT_OUTCOMES.map(({ key, label, help }) => (
        <Swatch
          key={key}
          label={label}
          help={help}
          color={outcomeInk(theme, key)}
        />
      ))}
    </Box>
  )
}

// The loaded rows rolled up, so the share routed reads without adding up a
// column. Over these spans only — never a figure for the window.
const Summary = ({ rows }) => {
  const totals = useMemo(() => {
    const sum = {
      ms: 0,
      speech: 0,
      routed: 0,
      cards: 0,
      usd: 0,
      priced: 0,
      floor: false,
    }
    rows.forEach((row) => {
      const ms = row.duration_ms || 0
      sum.ms += ms
      sum.speech += ms * (row.speech_fraction || 0)
      sum.routed += ms * (row.routed_fraction || 0)
      sum.cards += row.selection?.outcomes?.cut || 0
      if (row.cost_usd != null) {
        sum.usd += row.cost_usd
        sum.priced += 1
      }
      sum.floor = sum.floor || row.cost_floor
    })
    return sum
  }, [rows])
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      These {fmtCount(rows.length)} span{rows.length === 1 ? '' : 's'}:{' '}
      <b>{fmtSeconds(totals.ms / 1000)}</b> of audio ·{' '}
      <b>{fmtPct(totals.ms ? totals.speech / totals.ms : null)}</b> speech ·{' '}
      <b>{fmtPct(totals.ms ? totals.routed / totals.ms : null)}</b> routed ·{' '}
      <b>{fmtCount(totals.cards)}</b> card{totals.cards === 1 ? '' : 's'} ·{' '}
      <b>
        {totals.priced
          ? `${totals.floor ? '≥ ' : ''}${usd(totals.usd)}`
          : 'nothing billed'}
      </b>
    </Typography>
  )
}

const Header = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: COLUMNS,
      columnGap: 1.5,
      px: 1,
      pb: 0.5,
    }}
  >
    {['Recorded', 'Length', 'Speech', 'Routed', 'Cards', 'Cost'].map(
      (label) => (
        <Typography key={label} variant="caption" color="text.secondary">
          {label}
        </Typography>
      )
    )}
  </Box>
)

const FleetSpans = () => {
  const [rows, setRows] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [error, setError] = useState(null)
  const [fresh, setFresh] = useState(() => new Set())
  // Mirrors `rows` so a poll can merge without a state updater with effects.
  const rowsRef = useRef([])
  // Every row key shown so far; null until the first page lands, so the first
  // page does not flash as though it had all just arrived.
  const seen = useRef(null)

  const show = useCallback((next) => {
    rowsRef.current = next
    setRows(next)
  }, [])

  const refresh = useCallback(async () => {
    try {
      const data = await api.streamMetrics.getRoutingFleet({ limit: PAGE })
      const latest = data.rows
      const keys = latest.map(rowKey)
      setFresh(
        seen.current
          ? new Set(keys.filter((key) => !seen.current.has(key)))
          : new Set()
      )
      seen.current = new Set([...(seen.current || []), ...keys])
      // Older pages the reader loaded stay under the newest page, unless so
      // much arrived since the last poll that the two no longer meet.
      const prev = rowsRef.current
      const oldest = latest.length
        ? instant(latest[latest.length - 1].routed_at)
        : null
      if (
        prev.length <= PAGE ||
        oldest == null ||
        instant(prev[0].routed_at) < oldest
      ) {
        show(latest)
        setCursor(data.next_before)
      } else {
        show([
          ...latest,
          ...prev.filter((row) => instant(row.routed_at) < oldest),
        ])
      }
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [show])

  useEffect(() => {
    refresh()
    const timer = setInterval(refresh, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [refresh])

  const loadOlder = async () => {
    setLoadingOlder(true)
    try {
      const data = await api.streamMetrics.getRoutingFleet({
        limit: PAGE,
        before: cursor,
      })
      show([...rowsRef.current, ...data.rows])
      setCursor(data.next_before)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoadingOlder(false)
    }
  }

  if (error && !rows.length) {
    return (
      <Typography variant="body2" color="error.main">
        {error}
      </Typography>
    )
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {loading ? 'Loading…' : 'The router has finished no spans yet.'}
      </Typography>
    )
  }

  return (
    <Box>
      <Legend />
      <Summary rows={rows} />
      {error && (
        <Typography variant="caption" color="error.main" display="block">
          {error}
        </Typography>
      )}
      <Header />
      {rows.map((row) => (
        <FleetRow key={rowKey(row)} row={row} fresh={fresh.has(rowKey(row))} />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!cursor || loadingOlder}
          onClick={loadOlder}
        >
          {cursor ? 'Load older' : 'No older spans'}
        </Button>
        {loadingOlder && <CircularProgress size={16} />}
      </Box>
    </Box>
  )
}

export default FleetSpans
